import { getCheckServiceParams, getPublishTerms } from './utils';
import { changeAuctionBetSteps } from '../../../actions/auction';
import { changeCurrentBet } from '../../auction';
import { checkServiceFail, checkServiceStart, checkServiceSuccess } from '../../../actions/checkoutInfo';
import { setPromotionTypeChangeReason } from '../../../actions/publishTerms';
import { selectCurrentBet, selectIsAuctionSelected } from '../../../selectors/auction';
import { selectLocationIds, selectOfferCoordinatesWithFallback, selectPhotos } from '../../../selectors/offer';
import {
  selectActivePackage,
  selectActiveService,
  selectIsIncreaseToMinimalTopBet,
  selectIsWithoutServices,
  selectPublicationPeriod,
  selectPublicationTokens,
} from '../../../selectors/publishTerms';
import { IAppState } from '../../../../AppModulesWrapper';
import { selectTermsDurationInDays } from '../../../selectors/termsDurations';
import { checkService as checkServiceRequest } from '../../../services/checkService';
import { TThunkAction } from '../../../../common/types/thunk';
import { selectDynamicPricingData } from '../../../selectors/dynamicPricing';
import { ECheckServiceErrorCode, ECheckServiceStatus } from '../../../types/reducers/checkoutInfo';
import { selectModulePublishTermsResponse } from '../../../selectors/module';
import { changeSelectedServiceAfterCheckService } from '../changeSelectedServiceAfterCheckService';
import { updateBalance, updateRecommendedDepositAmount } from '../../../actions/balance';
import { selectActivePlacingType } from '../../../reducers/v2/paidPublication/selectors';
import { selectMinimalBet } from '../../../reducers/v2/promotion/selectors';
import { selectPlacingMoneySource } from '../../../reducers/v2/moneySources/selectors';
import { logPublicationTokens } from '../../../utils/logPublicationTokens';
import { loadPublishTerms } from '../../publishTerms/loadPublishTerms';

export function checkService(isInstant: boolean = false): TThunkAction<Promise<void>> {
  return async function (dispatch, getState, { httpApi, logger, telemetry, appEvent }) {
    const state: IAppState = getState();

    const service = selectActiveService(state);
    const servicePackage = selectActivePackage(state);
    const publicationPeriod = selectPublicationPeriod(state);
    const isAuctionSelected = selectIsAuctionSelected(state);
    const placingType = selectActivePlacingType(state);
    const locationPath = selectLocationIds(state);
    const photos = selectPhotos(state);
    const coordinates = selectOfferCoordinatesWithFallback(state);
    const extraData = selectDynamicPricingData(state);
    const moneySource = selectPlacingMoneySource(state);
    const increaseToMinimalTopBet = selectIsIncreaseToMinimalTopBet(state);
    const isWithoutServices = selectIsWithoutServices(state);
    const minimalBet = selectMinimalBet(state);
    const termsDuration = selectTermsDurationInDays(state);
    const publicationTokens = selectPublicationTokens(state);
    const currentBet = selectCurrentBet(state);

    logPublicationTokens(logger, state, publicationTokens, 'checkService thunk');

    const { offer, checkoutInfo, auction, calltracking } = state.servicePickerWidget;

    // В саге checkService есть проверка на источник экшена, надо перенести
    const isServiceChanged = false;

    const terms = getPublishTerms({
      service,
      servicePackage,
      publicationPeriod,
      placingType,
      auction,
      isAuctionSelected,
      calltracking,
      increaseToMinimalTopBet,
      isWithoutServices,
      minimalBet,
      termsDuration,
      currentBet,
    });

    dispatch(checkServiceStart());
    appEvent.onCheckServiceStatusChange(ECheckServiceStatus.pending);

    const parameters = getCheckServiceParams({
      offer,
      locationPath,
      checkoutInfo,
      terms,
      isServiceChanged,
      photosCount: photos.length,
      coordinates,
      extraData,
      moneySource,
      publicationTokens,
    });

    const response = await checkServiceRequest({ httpApi, logger, telemetry, parameters });

    return new Promise(resolve => {
      const delay = isInstant ? 0 : 1000;

      setTimeout(() => {
        if (response.success) {
          const { selectedPromotionInfo, auction, balance, recommendedDepositAmount } = response;

          dispatch(updateRecommendedDepositAmount(recommendedDepositAmount));

          dispatch(setPromotionTypeChangeReason(selectedPromotionInfo.promotionTypeChangeReason));
          dispatch(
            changeSelectedServiceAfterCheckService({
              publicationType: selectedPromotionInfo.publicationType,
              promotionType: selectedPromotionInfo.promotionType,
              serviceId: selectedPromotionInfo.serviceId,
            }),
          );

          if (auction) {
            dispatch(changeAuctionBetSteps({ previousBets: auction.previousBets, nextBets: auction.nextBets }));
            dispatch(changeCurrentBet({ afterCheckService: true, bet: auction.currentBet }));
          }

          dispatch(checkServiceSuccess(response.payload));

          dispatch(updateBalance(balance));

          const terms = selectModulePublishTermsResponse(getState(), logger);
          appEvent.onCheckServiceChange(terms);

          appEvent.onCheckServiceStatusChange(ECheckServiceStatus.success);

          // В саге checkService на этом месте происходит трекинг событий изменения ставки
        } else {
          if (response.payload === ECheckServiceErrorCode.tokenExpired) {
            return dispatch(loadPublishTerms({ isServicesReload: true }));
          }

          dispatch(checkServiceFail(response.payload));
          appEvent.onCheckServiceStatusChange(ECheckServiceStatus.fail);
        }

        return resolve();
        // При быстром ответе сервера лоадер появляется на долю секунд в поле ставки, из-за чего происходит мерцание.
        // Для хорошего UX ограничиваем минимально возможное выполнение запроса
      }, delay);
    });
  };
}
